import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { CellMeasurer, CellMeasurerCache, List, AutoSizer } from 'react-virtualized';
import { MeasuredCellParent } from 'react-virtualized/dist/es/CellMeasurer';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { Box } from '@mui/material';

import { getProviders } from '../actions';
import { Plan } from '../../planSelect/types/plan';
import LoadingSpinner from '../../components/LoadingSpinner';
import {
  Provider,
  SearchRadius,
  SortCriteria,
  SortDirection,
  TaxonomyDetailsMap,
  RootState,
  Accessibility,
} from '../types/providerSearch';
import ProviderCard from './ProviderCard';

const ListWrapper = styled(Box)({
  marginLeft: '1.5rem',
  width: '100%',
  height: '100%',
  paddingBottom: '2rem',
});

const ProviderCardWrapper = styled(Box)({
  width: 'calc(50% - 10px)',
  marginBottom: '10px',
  flex: '0 1 calc(50% - 10px)',
});

const Row = styled(Box)({
  display: 'flex',
  gap: '20px',
  width: '100%',
});

const NoProviders = styled(Typography)({
  width: '100%',
  margin: '1rem 2rem',
  textAlign: 'center',
});

const LoadingSpinnerContainer = styled(Box)({
  margin: '3rem auto 0 auto',
});

const IllustrationWrapper = styled(Box)({
  width: '100%',
  display: 'grid',
  placeContent: 'center',
});

const PaginationContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'end',
  gap: '1rem',
  marginRight: '2rem',
});

const PageSizeContainer = styled(Box)({
  display: 'flex',
  gap: '0.5rem',
  marginLeft: '0.5rem',
  alignItems: 'center',
});

const PageSizeOption = styled('span')<{ active?: boolean }>(({ active }) => ({
  borderRight: '1px solid #2B629B',
  paddingRight: '0.2rem',
  color: active ? 'black' : '#2B629B',
  cursor: 'pointer',
}));

const cache = new CellMeasurerCache({
  defaultHeight: 200,
  fixedWidth: true,
  minHeight: 180,
});

type ActualSizing = {
  actualHeight: number;
  actualWidth: number;
};

function detectTestSizing(height: number, width: number): ActualSizing {
  let actualWidth = width;
  let actualHeight = height;
  if (process.env.NODE_ENV === 'test') {
    actualWidth = window.innerWidth;
    actualHeight = window.innerHeight;
  }
  return { actualHeight, actualWidth };
}

const mapStateToProps = ({ providerSearch, planSelect }: RootState): StateProps => ({
  selectedZipCode: providerSearch.selectedZipCode,
  searchInput: providerSearch.searchInput,
  radius: providerSearch.radius,
  isLoading: providerSearch.isLoadingProviderList,
  providerList: providerSearch.fullList, // should already be paginated from backend
  sortDirection: providerSearch.sortDirection,
  totalPages: providerSearch.totalPages,
  sortCriteria: providerSearch.sortCriteria,
  selectedAccessibilities: providerSearch.selectedAccessibilities,
  taxonomyDetailsMap: providerSearch.taxonomyDetailsMap,
  selectedProviderType: providerSearch.selectedProviderType,
  selectedPlanID: providerSearch.selectedPlanID,
  providergendercode: providerSearch.providergendercode,
  entitytype: providerSearch.entitytype,
  selectedProviderSpecialty: providerSearch.selectedProviderSpecialty,
  searchClicked: providerSearch.searchClicked,
  plans: planSelect.plans,
  planID: planSelect.selectedPlan?.id,
});

type StateProps = {
  selectedZipCode: string;
  searchInput: string;
  totalPages: number;
  radius: SearchRadius;
  isLoading: boolean;
  providerList: Provider[];
  sortDirection: SortDirection;
  sortCriteria: SortCriteria;
  selectedAccessibilities: Accessibility[];
  taxonomyDetailsMap: TaxonomyDetailsMap;
  selectedProviderType: string;
  selectedPlanID: string;
  selectedProviderSpecialty: string;
  searchClicked: boolean;
  providergendercode: string;
  entitytype: string;
  plans: Plan[];
  planID: any;
};

const mapDispatchToProps = {
  getProviders,
};

type Props = StateProps & typeof mapDispatchToProps;

export function ProviderList({
  providerList,
  searchInput,
  selectedZipCode,
  totalPages,
  isLoading,
  radius,
  planID,
  sortDirection,
  sortCriteria,
  providergendercode,
  entitytype,
  selectedAccessibilities,
  taxonomyDetailsMap,
  selectedProviderType,
  selectedProviderSpecialty,
  searchClicked,
  selectedPlanID,
  plans: _plans,
  getProviders,
}: Props): JSX.Element {
  const listRef = useRef<List>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  useEffect(() => {
    cache.clearAll();
    if (listRef.current) {
      listRef.current.forceUpdateGrid();
      listRef.current.scrollToPosition(0);
    }
    setPage(1);
    if (selectedZipCode && selectedProviderType && searchClicked)
      getProviders(
        planID,
        selectedZipCode,
        radius,
        searchInput,
        pageSize,
        1,
        selectedAccessibilities,
        selectedProviderType,
        selectedProviderSpecialty,
        providergendercode,
        entitytype
      );
  }, [
    selectedZipCode,
    radius,
    sortDirection,
    sortCriteria,
    selectedAccessibilities,
    selectedProviderType,
    selectedProviderSpecialty,
    selectedPlanID,
    providergendercode,
    entitytype,
  ]);

  useEffect(() => {
    cache.clearAll();
    if (listRef.current) {
      listRef.current.forceUpdateGrid();
      listRef.current.scrollToPosition(0);
    }
    setPage(1);
  }, [searchInput]);

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
    getProviders(
      planID,
      selectedZipCode,
      radius,
      searchInput,
      size,
      1,
      selectedAccessibilities,
      selectedProviderType,
      selectedProviderSpecialty,
      providergendercode,
      entitytype
    );
  };

  const handleNextClick = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    getProviders(
      planID,
      selectedZipCode,
      radius,
      searchInput,
      pageSize,
      nextPage,
      selectedAccessibilities,
      selectedProviderType,
      selectedProviderSpecialty,
      providergendercode,
      entitytype
    );
  };

  const handlePreviousClick = () => {
    const prevPage = page - 1;
    setPage(prevPage);
    getProviders(
      planID,
      selectedZipCode,
      radius,
      searchInput,
      pageSize,
      prevPage,
      selectedAccessibilities,
      selectedProviderType,
      selectedProviderSpecialty,
      providergendercode,
      entitytype
    );
  };

  if (isLoading) {
    return (
      <LoadingSpinnerContainer>
        <LoadingSpinner />
      </LoadingSpinnerContainer>
    );
  }

  if (
    providerList.length === 0 &&
    selectedZipCode !== '' &&
    selectedProviderType !== '' &&
    searchClicked
  ) {
    return (
      <NoProviders>
        No providers found with given filters
        {searchInput ? ` and search text of "${searchInput}"` : ''}
      </NoProviders>
    );
  }

  if (providerList.length === 0 && !searchClicked) {
    return (
      <IllustrationWrapper>
        <img src="./images/ProviderSearchIllustration.svg" alt="ProviderSearchIllustration" />
      </IllustrationWrapper>
    );
  }

  const rowRenderer = ({ index, key, style, parent }: RowRendererProps): JSX.Element => {
    const provider1 = providerList[index * 2];
    const provider2 = providerList[index * 2 + 1];

    if (!provider1 && !provider2) return <div key={key} style={style} />;

    return (
      <CellMeasurer key={key} cache={cache} columnIndex={0} parent={parent} rowIndex={index}>
        {({ measure }) => (
          <Row style={style}>
            {provider1 && (
              <ProviderCardWrapper
                onClick={measure}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') measure();
                }}
                role="button"
                tabIndex={0}
              >
                <ProviderCard
                  provider={provider1}
                  isFirstCard={index === 0 && !provider2}
                  isLastCard={!provider2 && index === Math.floor((providerList.length - 1) / 2)}
                  category={taxonomyDetailsMap[provider1.primaryTaxonomy]?.category}
                  title={taxonomyDetailsMap[provider1.primaryTaxonomy]?.title}
                  selectedAccessibilities={selectedAccessibilities}
                />
              </ProviderCardWrapper>
            )}
            {provider2 && (
              <ProviderCardWrapper
                onClick={measure}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') measure();
                }}
                role="button"
                tabIndex={0}
              >
                <ProviderCard
                  provider={provider2}
                  isFirstCard={index === 0}
                  isLastCard={index === Math.floor((providerList.length - 1) / 2)}
                  category={taxonomyDetailsMap[provider2.primaryTaxonomy]?.category}
                  title={taxonomyDetailsMap[provider2.primaryTaxonomy]?.title}
                  selectedAccessibilities={selectedAccessibilities}
                />
              </ProviderCardWrapper>
            )}
          </Row>
        )}
      </CellMeasurer>
    );
  };

  type RowRendererProps = {
    index: number;
    key: string;
    style: React.CSSProperties;
    parent: MeasuredCellParent;
  };

  const totalRecords = totalPages * pageSize;
  const startRecord = (page - 1) * pageSize + 1;
  const endRecord = Math.min(page * pageSize, totalRecords);

  return (
    <ListWrapper>
      <PaginationContainer style={{ marginBottom: '0.5rem' }}>
        <Button variant="outlined" onClick={handlePreviousClick} disabled={page === 1}>
          Previous
        </Button>
        <Button variant="outlined">{`${startRecord}-${endRecord} of ${totalRecords}`}</Button>
        <Button variant="outlined" onClick={handleNextClick} disabled={page === totalPages}>
          Next
        </Button>

        <PageSizeContainer>
          <span>Results per page </span>
          {[50, 75, 100].map(size => (
            <PageSizeOption
              key={size}
              active={pageSize === size}
              onClick={() => handlePageSizeChange(size)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handlePageSizeChange(size);
                }
              }}
              role="button"
              tabIndex={0}
            >
              {size}
            </PageSizeOption>
          ))}
        </PageSizeContainer>
      </PaginationContainer>

      <AutoSizer>
        {({ width, height }) => {
          const { actualHeight, actualWidth } = detectTestSizing(height, width);
          return (
            <List
              ref={listRef}
              style={{ outline: 'none', paddingBottom: '2rem' }}
              height={actualHeight}
              width={actualWidth}
              rowCount={Math.ceil(providerList.length / 2)}
              rowHeight={200}
              rowRenderer={rowRenderer}
            />
          );
        }}
      </AutoSizer>
    </ListWrapper>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(ProviderList);
